import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';

import { EMMATableAction } from './emma-table.types';

@Component({
    selector: 'emma-table-row-actions',
    templateUrl: './emma-table-row-actions.component.html',
    standalone: false
})
export class EMMATableRowActionsComponent {
  private static readonly VIEWPORT_BOUNDS_CACHE_MS = 250;
  private static viewportBoundsCache?: {
    bounds: { top: number; bottom: number; padding: number };
    expiresAt: number;
  };

  @ViewChild('menuElement')
  set menuElementRef(value: ElementRef<HTMLElement> | undefined) {
    this.menuElement = value;
    if (value && this.pendingMeasure) {
      requestAnimationFrame(() => this.updateMeasuredMenuPosition());
    }
  }
  @Input() row: unknown;
  @Input() index = -1;
  @Input() actions: EMMATableAction[] = [];
  @Input() showMenu = false;

  @Output() active = new EventEmitter<boolean>();

  menuStyles: Record<string, string> = {};
  menuBridgeStyles: Record<string, string> = {};
  private closeMenuTimeout?: ReturnType<typeof setTimeout>;
  private menuElement?: ElementRef<HTMLElement>;
  private lastButtonElement?: HTMLElement;
  private lastButtonRect?: DOMRect;
  private lastViewportBounds?: { top: number; bottom: number; padding: number };
  private pendingMeasure = false;

  /** Show actions menu */
  onRowActionsMenu(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.clearCloseMenuTimeout();
    const button = event.currentTarget as HTMLElement | null;
    if (this.showMenu && button && this.lastButtonElement === button && !this.pendingMeasure) {
      return;
    }
    this.updateMenuDirection(event);
    this.active.emit(true);
  }
  onRowActionsMenuEnter() {
    this.clearCloseMenuTimeout();
  }
  /** Hide actions menu */
  onRowActionsMenuClose() {
    this.clearCloseMenuTimeout();
    this.active.emit(false);
  }
  onRowActionsMenuLeave() {
    this.clearCloseMenuTimeout();
    this.closeMenuTimeout = setTimeout(() => this.active.emit(false), 50);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    EMMATableRowActionsComponent.viewportBoundsCache = undefined;
  }

  /** Emit action on row */
  onRowActionClick(_event: Event, row: unknown, action: EMMATableAction) {
    // event.stopPropagation();
    if (action.$click) {
      action.$click.emit(row);
    }
  }
  /** Emit action on table */
  onActionClick(_event: Event, action: EMMATableAction) {
    // event.stopPropagation();
    if (action.$click) {
      action.$click.emit();
    }
  }

  private updateMenuDirection(event: MouseEvent): void {
    const button = event.currentTarget as HTMLElement | null;
    if (!button) {
      this.menuStyles = {};
      this.menuBridgeStyles = {};
      this.lastButtonElement = undefined;
      return;
    }

    this.lastButtonElement = button;
    const buttonRect = button.getBoundingClientRect();
    this.lastButtonRect = buttonRect;
    const menuHeight = this.actions.length * 44;
    const menuGap = 12;
    const viewportBounds = this.getViewportBounds();
    this.lastViewportBounds = viewportBounds;
    const spaceBelow = viewportBounds.bottom - buttonRect.bottom;
    const spaceAbove = buttonRect.top - viewportBounds.top;
    const openDown = spaceBelow >= menuHeight + menuGap || spaceBelow >= spaceAbove;
    const top = openDown
      ? Math.max(viewportBounds.top, buttonRect.top - 8)
      : Math.max(viewportBounds.top, buttonRect.bottom - menuHeight + 8);
    const left = Math.min(buttonRect.right + menuGap, window.innerWidth - 220 - viewportBounds.padding);
    const bridgeLeft = buttonRect.right - 2;
    const bridgeTop = Math.min(buttonRect.top, top);
    const bridgeHeight = Math.max(buttonRect.bottom, top + menuHeight) - bridgeTop;

    this.menuStyles = {
      position: 'fixed',
      top: `${top}px`,
      left: `${Math.max(viewportBounds.padding, left)}px`,
      'max-height': `${viewportBounds.bottom - viewportBounds.top}px`,
      visibility: 'hidden',
    };
    this.menuBridgeStyles = {
      position: 'fixed',
      top: `${bridgeTop}px`,
      left: `${bridgeLeft}px`,
      width: `${Math.max(18, left - bridgeLeft + 6)}px`,
      height: `${Math.max(buttonRect.height, bridgeHeight)}px`,
    };
    this.pendingMeasure = true;
  }

  private updateMeasuredMenuPosition(): void {
    const menu = this.menuElement?.nativeElement;
    const buttonRect = this.lastButtonRect;
    const viewportBounds = this.lastViewportBounds ?? this.getViewportBounds();
    if (!menu || !buttonRect) {
      return;
    }

    const menuGap = 12;
    const measuredMenuHeight = menu.offsetHeight;
    const measuredMenuWidth = menu.offsetWidth;
    const spaceBelow = viewportBounds.bottom - buttonRect.bottom;
    const spaceAbove = buttonRect.top - viewportBounds.top;
    const openDown =
      spaceBelow >= measuredMenuHeight + menuGap || spaceBelow >= spaceAbove;
    const top = openDown
      ? Math.max(viewportBounds.top, buttonRect.top - 8)
      : Math.max(viewportBounds.top, buttonRect.bottom - measuredMenuHeight + 8);
    const left = Math.min(
      buttonRect.right + menuGap,
      window.innerWidth - measuredMenuWidth - viewportBounds.padding
    );
    const bridgeLeft = buttonRect.right - 2;
    const bridgeTop = Math.min(buttonRect.top, top);
    const bridgeHeight = Math.max(buttonRect.bottom, top + measuredMenuHeight) - bridgeTop;

    this.menuStyles = {
      ...this.menuStyles,
      top: `${top}px`,
      left: `${Math.max(viewportBounds.padding, left)}px`,
      'max-height': `${viewportBounds.bottom - viewportBounds.top}px`,
      visibility: 'visible',
    };
    this.menuBridgeStyles = {
      ...this.menuBridgeStyles,
      top: `${bridgeTop}px`,
      left: `${bridgeLeft}px`,
      width: `${Math.max(18, left - bridgeLeft + 6)}px`,
      height: `${Math.max(buttonRect.height, bridgeHeight)}px`,
    };
    this.pendingMeasure = false;
  }

  private clearCloseMenuTimeout(): void {
    if (this.closeMenuTimeout) {
      clearTimeout(this.closeMenuTimeout);
      this.closeMenuTimeout = undefined;
    }
  }

  private getViewportBounds(): { top: number; bottom: number; padding: number } {
    const cachedBounds = EMMATableRowActionsComponent.viewportBoundsCache;
    const now = Date.now();
    if (cachedBounds && cachedBounds.expiresAt > now) {
      return cachedBounds.bounds;
    }

    const padding = 16;
    const blockingSelectors = ['.m-header', '.m-subheader', '.emmaFilterBar--sticky'];
    const top = blockingSelectors
      .flatMap((selector) => Array.from(document.querySelectorAll<HTMLElement>(selector)))
      .map((element) => element.getBoundingClientRect())
      .filter((rect) => rect.height > 0)
      .reduce((maxBottom, rect) => Math.max(maxBottom, rect.bottom), padding);

    const bounds = {
      top: Math.max(padding, top),
      bottom: window.innerHeight - padding,
      padding,
    };
    EMMATableRowActionsComponent.viewportBoundsCache = {
      bounds,
      expiresAt: now + EMMATableRowActionsComponent.VIEWPORT_BOUNDS_CACHE_MS,
    };
    return bounds;
  }
}
